import React, { useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import {
  Box,
  Typography,
  Button,
  Iconography,
  Table,
  Image,
  Dropdown,
  formatPrice,
} from '@lucid/lego';
import airSilhouette from '../../../../../../assets/lucid-air.png';
import gravitySilhouette from '../../../../../../assets/lucid-gravity.png';
import type { TableColumn } from '@lucid/lego';
import { useCartContext, useDeleteCartItem, useUpdateCartItem } from '@lucid/cart';
import type { OrderPreference } from '../../../../../../api/services/lupp/order/importer/dto.importer';
import type { CheckoutItem } from '../types';
import styles from './VehicleCard.module.scss';

type VehicleRow = {
  vin: string;
  batchNumber: string;
  shortCode: string;
  wholesalePrice: string;
  model: string;
  trim: string;
  exteriorColor: string;
  drivetrain: string;
  interiorColor: string;
  wheels: string;
  appearance: string;
  roof: string;
  adas: string;
  soundSystem: string;
  frontSeats: string;
  comfortPackage: string;
  range: string;
  power: string;
};

/** Builds the Order Type dropdown options (user-facing label for orderPreference). */
const orderPreferenceOptions = (
  labels?: Record<string, string>
): { value: OrderPreference; label: string }[] => [
  { value: 'CUSTOMER', label: labels?.['vehicle.orderType.customer'] ?? 'Customer' },
  { value: 'DEMO', label: labels?.['vehicle.orderType.demo'] ?? 'Demo' },
  { value: 'INVENTORY', label: labels?.['vehicle.orderType.inventory'] ?? 'Inventory' },
  { value: 'PRESS', label: labels?.['vehicle.orderType.press'] ?? 'Press' },
  {
    value: 'SERVICE_LOANER',
    label: labels?.['vehicle.orderType.serviceLoaner'] ?? 'Service Loaner',
  },
];

const unitCountForItem = (item: CheckoutItem): number => {
  const itemCtx = item.itemContext as Record<string, unknown> | undefined;
  const orderType = ((itemCtx?.orderType as string) ?? 'INVENTORY').toUpperCase();
  return orderType === 'BUILD' ? (item.quantityOrdered ?? 1) : 1;
};

const vehicleDetailsColumns = (labels?: Record<string, string>): TableColumn<VehicleRow>[] => [
  { key: 'model', title: labels?.['vehicle.model'] ?? 'Model' },
  { key: 'trim', title: labels?.['vehicle.trim'] ?? 'Trim' },
  { key: 'exteriorColor', title: labels?.['vehicle.exteriorColor'] ?? 'Exterior Color' },
  { key: 'drivetrain', title: labels?.['vehicle.drivetrain'] ?? 'Drivetrain' },
  { key: 'interiorColor', title: labels?.['vehicle.interiorTheme'] ?? 'Interior Theme' },
  { key: 'wheels', title: labels?.['vehicle.wheels'] ?? 'Wheels' },
  { key: 'appearance', title: labels?.['vehicle.appearance'] ?? 'Appearance' },
  { key: 'roof', title: labels?.['vehicle.roof'] ?? 'Roof' },
  { key: 'adas', title: labels?.['vehicle.driverAssistance'] ?? 'Driver Assistance' },
  { key: 'soundSystem', title: labels?.['vehicle.soundSystem'] ?? 'Sound System' },
  { key: 'frontSeats', title: labels?.['vehicle.seating'] ?? 'Seating' },
  {
    key: 'comfortPackage',
    title: labels?.['vehicle.comfortConveniencePackage'] ?? 'Comfort & Convenience Package',
  },
  { key: 'range', title: labels?.['vehicle.range'] ?? 'Range' },
  { key: 'power', title: labels?.['vehicle.power'] ?? 'Power' },
];

const inventoryTableColumns = (labels?: Record<string, string>): TableColumn<VehicleRow>[] => [
  { key: 'vin', title: labels?.['vehicle.vin'] ?? 'VIN' },
  { key: 'batchNumber', title: labels?.['vehicle.batch'] ?? 'Batch #' },
  { key: 'wholesalePrice', title: labels?.['vehicle.wholesalePrice'] ?? 'Wholesale Price' },
];

const buildTableColumns = (labels?: Record<string, string>): TableColumn<VehicleRow>[] => [
  { key: 'shortCode', title: labels?.['vehicle.shortCode'] ?? 'Short Code' },
  { key: 'wholesalePrice', title: labels?.['vehicle.wholesalePrice'] ?? 'Wholesale Price' },
];

export type VehicleCardProps = {
  items: CheckoutItem[];
  locale?: string;
  theme: 'dark' | 'light';
  isExpanded: boolean;
  onToggle: () => void;
  customerId: string;
  onAllItemsDeleted?: () => void;
  labels?: Record<string, string>;
  orderPrefs?: Record<string, OrderPreference>;
  onOrderPreferenceChange?: (itemId: string, unitIndex: number, value: OrderPreference) => void;
};

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

/**
 * Displays a single cart item as a collapsible vehicle card with a VIN table and delete action.
 * @component
 * @param {VehicleCardProps} props
 * @returns {JSX.Element}
 * @example
 * <VehicleCard item={cartItem} theme="light" isExpanded={false} onToggle={() => {}} customerId="123" />
 */
const VehicleCard: React.FC<VehicleCardProps> = ({
  items,
  locale,
  theme,
  isExpanded,
  onToggle,
  customerId,
  onAllItemsDeleted,
  labels,
  orderPrefs = {},
  onOrderPreferenceChange,
}) => {
  const { activeCart, refetchCart } = useCartContext();
  const cartId = activeCart?.cartId ?? '';
  const totalCartItems = activeCart?.items?.length ?? 0;
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deleteCartItem } = useDeleteCartItem();
  const { mutateAsync: updateCartItem } = useUpdateCartItem();

  // Build a flat row→CartItem mapping that mirrors the table data expansion.
  // BUILD items expand to N rows (quantityOrdered), INVENTORY items expand to 1 row each.
  // Each entry tracks the unit index within its CartItem so per-vehicle order
  // preferences can be addressed individually.
  const rowEntries = useMemo(
    () =>
      items.flatMap((item) =>
        Array.from({ length: unitCountForItem(item) }, (_, unitIndex) => ({ item, unitIndex }))
      ),
    [items]
  );

  const firstItem = items[0];
  const cardKey = firstItem?.itemCode ?? 'unknown';
  const ctx = firstItem?.itemContext as Record<string, unknown> | undefined;
  const orderType = ((ctx?.orderType as string) ?? 'INVENTORY').toUpperCase();
  const modelYear = (ctx?.modelYear as string) ?? (ctx?.year as string) ?? '';
  const model = (ctx?.model as string) ?? '';
  const trim = (ctx?.trim as string) ?? '';
  const exteriorColor =
    (ctx?.exteriorColor as string) ||
    (ctx?.vehicleOptions as Array<{ type: string; name: string }> | undefined)?.find(
      (o) => o.type === 'LUCID_EXT_COLOR'
    )?.name ||
    '';
  // For BUILD orders, trim already contains the model name — omit model to avoid duplication
  const titleParts = orderType === 'BUILD' ? [modelYear, trim] : [modelYear, model, trim];
  const vehicleTitle = titleParts.filter(Boolean).join(' ') || (firstItem?.itemName ?? '');
  const qty = rowEntries.length;
  const totalOwed = items.reduce(
    (sum, item) =>
      sum + (item.lineTotalAmount ?? (item.unitPrice ?? 0) * (item.quantityOrdered ?? 0)),
    0
  );

  const renderChainUrl = (ctx?.imageUrl as string) ?? (ctx?.vehicleImage as string) ?? '';
  const fallbackSrc = model.toLowerCase().includes('gravity') ? gravitySilhouette : airSilhouette;

  const vehicleImageProps = {
    uri_desktop: { '1x': renderChainUrl || fallbackSrc, '2x': renderChainUrl || fallbackSrc },
    uri_mobile: { '1x': renderChainUrl || fallbackSrc, '2x': renderChainUrl || fallbackSrc },
  };

  const handleDelete = useCallback(async () => {
    if (selectedRows.length === 0) return;
    setIsDeleting(true);
    try {
      // Tally selected rows per CartItem, tracking orderType per item
      const buildCounts = new Map<string, { item: CheckoutItem; count: number }>();
      const inventoryIds = new Set<string>();

      for (const rowIndex of selectedRows) {
        const item = rowEntries[rowIndex]?.item;
        if (!item?.itemId) continue;
        const itemCtx = item.itemContext as Record<string, string> | undefined;
        const orderType = (itemCtx?.orderType ?? 'INVENTORY').toUpperCase();

        if (orderType === 'BUILD') {
          const entry = buildCounts.get(item.itemId);
          if (entry) {
            entry.count += 1;
          } else {
            buildCounts.set(item.itemId, { item, count: 1 });
          }
        } else {
          inventoryIds.add(item.itemId);
        }
      }

      // Track how many distinct CartItems will be fully removed
      let itemsRemoved = inventoryIds.size;
      const operations: Promise<unknown>[] = [];

      // INVENTORY → DELETE
      for (const itemId of inventoryIds) {
        operations.push(deleteCartItem({ cartId, itemId, customerId }));
      }

      // BUILD → PATCH with reduced quantity (backend deletes when qty reaches 0)
      for (const [, { item, count }] of buildCounts) {
        const newQty = Math.max(0, (item.quantityOrdered ?? 1) - count);
        if (newQty === 0) itemsRemoved += 1;
        operations.push(
          updateCartItem({ cartId, itemId: item.itemId!, quantityOrdered: newQty, customerId })
        );
      }

      await Promise.all(operations);
      setSelectedRows([]);

      if (itemsRemoved >= totalCartItems) {
        onAllItemsDeleted?.();
      } else {
        void refetchCart();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedRows,
    rowEntries,
    cartId,
    customerId,
    deleteCartItem,
    updateCartItem,
    refetchCart,
    totalCartItems,
    onAllItemsDeleted,
  ]);

  return (
    <Box
      className={styles.vehicleCard}
      pl={16}
      pr={16}
      theme={theme}
      data-testid={`vehicle-card-${cardKey}`}
    >
      <Box className={styles.vehicleCardHeader} onClick={onToggle}>
        <Typography variant="subtitle" size="small" color="black" theme={theme}>
          {vehicleTitle}
        </Typography>
        <Iconography
          category="arrows"
          name="chevron-down"
          color="black"
          className={classnames(styles.chevronIcon, { [styles.chevronExpanded]: isExpanded })}
        />
      </Box>

      {/* Always-visible body */}
      <Box className={styles.vehicleCardBody} onClick={onToggle}>
        {/* Vehicle image */}
        <Box className={styles.vehicleImage}>
          <Image
            {...vehicleImageProps}
            fallbackSrc={fallbackSrc}
            alt={vehicleTitle}
            theme={theme}
            useAspectRatio={false}
            showSkeleton={false}
          />
        </Box>

        {/* QTY badge */}
        <Box className={styles.qtyBadge} data-testid={`vehicle-card-qty-${cardKey}`}>
          <Typography variant="body" size="extraSmall" color="white" theme={theme}>
            {labels?.['vehicle.qty'] ?? 'QTY'}
          </Typography>
          <Typography variant="headline" size="small" color="white" theme={theme}>
            {qty}
          </Typography>
        </Box>

        {/* Data columns */}
        <Box className={styles.vehicleDataCols}>
          {exteriorColor && (
            <Box className={styles.vehicleDataCol}>
              <Typography variant="body" size="extraSmall" color="gray-5" theme={theme}>
                {labels?.['vehicle.exteriorColor'] ?? 'EXTERIOR COLOR'}
              </Typography>
              <Typography variant="body" size="small" color="black" theme={theme}>
                {exteriorColor}
              </Typography>
            </Box>
          )}
          <Box className={styles.vehicleDataCol}>
            <Typography variant="body" size="extraSmall" color="gray-5" theme={theme}>
              {labels?.['vehicle.totalOwed'] ?? 'TOTAL OWED'}
            </Typography>
            <Typography variant="body" size="small" color="black" theme={theme}>
              {formatDisplayPrice(totalOwed, locale ?? 'de-de')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Expanded-only — VIN records table */}
      {isExpanded && (
        <Box className={styles.vehicleTable} data-testid={`vehicle-table-${cardKey}`}>
          <Table
            theme="light"
            compact
            dataTestId={`vehicle-table-inner-${cardKey}`}
            columns={[
              ...(orderType === 'BUILD'
                ? buildTableColumns(labels)
                : inventoryTableColumns(labels)),
              {
                key: 'orderType',
                title: labels?.['vehicle.orderType'] ?? 'Order Type',
                render: (_value, _record, index) => {
                  const entry = rowEntries[index];
                  if (!entry?.item?.itemId) return null;
                  const key = `${entry.item.itemId}:${entry.unitIndex}`;
                  return (
                    <Box data-testid={`order-type-dropdown-${cardKey}-${index}`}>
                      <Dropdown
                        theme="light"
                        size="small"
                        inline
                        showValidation={false}
                        options={orderPreferenceOptions(labels)}
                        selectedOption={orderPrefs[key] ?? 'CUSTOMER'}
                        handleSelect={(value) =>
                          onOrderPreferenceChange?.(
                            entry.item.itemId!,
                            entry.unitIndex,
                            value as OrderPreference
                          )
                        }
                      />
                    </Box>
                  );
                },
              },
              ...vehicleDetailsColumns(labels),
            ]}
            data={rowEntries.map(({ item }) => {
              const itemCtx = item.itemContext as Record<string, string> | undefined;
              return {
                vin: itemCtx?.vin || '-',
                batchNumber: itemCtx?.batchNumber ?? '-',
                shortCode: item.itemCode ?? '-',
                wholesalePrice: formatDisplayPrice(item.unitPrice ?? 0, locale ?? 'de-de'),
                model: (itemCtx?.model ?? '').replace(/\b\w/g, (c) => c.toUpperCase()),
                trim: itemCtx?.trim ?? '',
                exteriorColor: itemCtx?.exteriorColor || '-',
                drivetrain: itemCtx?.drivetrain || '-',
                interiorColor: itemCtx?.interiorColor || '-',
                wheels: itemCtx?.wheels || '-',
                appearance: itemCtx?.appearance || '-',
                roof: itemCtx?.roof || '-',
                adas: itemCtx?.adas || '-',
                soundSystem: itemCtx?.soundSystem || '-',
                frontSeats: itemCtx?.frontSeats || '-',
                comfortPackage: itemCtx?.comfortPackage || '-',
                range: itemCtx?.range || '-',
                power: itemCtx?.power || '-',
              };
            })}
            pagination={{ enabled: false }}
            columnConfig={{ enabled: true }}
            filter={{ enabled: false }}
            selection={{
              enabled: true,
              rows: selectedRows,
              onChange: setSelectedRows,
              showExport: false,
            }}
            toolbar={{
              content: (
                <Button
                  theme={theme}
                  size="small"
                  color="error-2"
                  dataTestId={`delete-cart-item-${cardKey}`}
                  gaComponent="order-builder-checkout"
                  handleClick={handleDelete}
                  disabled={selectedRows.length === 0 || isDeleting}
                  label={
                    isDeleting
                      ? (labels?.deleting ?? 'Deleting...')
                      : (labels?.['vehicle.delete'] ?? 'Delete')
                  }
                  iconLeft={{ category: 'global', name: 'trash' }}
                />
              ),
            }}
            labels={{
              manageColumns: labels?.['vehicle.manageColumns'] ?? 'Manage Columns',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VehicleCard;
